import React, { useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { RefreshCw, Download } from 'lucide-react';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { useWriterEnabled } from '@/hooks/useWriterEnabled';
import { RANGES, useWriterTelemetry } from '@/hooks/useWriterTelemetry';
import HealthStrip from '@/components/admin/writer-telemetry/HealthStrip';
import CostPanel from '@/components/admin/writer-telemetry/CostPanel';
import ReliabilityPanel from '@/components/admin/writer-telemetry/ReliabilityPanel';
import AgentPanel from '@/components/admin/writer-telemetry/AgentPanel';
import BreakdownTables from '@/components/admin/writer-telemetry/BreakdownTables';
import LatencyPanel from '@/components/admin/writer-telemetry/LatencyPanel';
import WriterCompileQueuePanel from '@/components/writer/WriterCompileQueuePanel';
import WriterRepairKillSwitches from '@/components/writer/WriterRepairKillSwitches';
import WriterPricingSuggester from '@/components/pricing/WriterPricingSuggester';
import { buildDaySeries, totalsFor } from '@/lib/writer/telemetryTypes';
import { exportCSV } from '@/utils/analytics-helpers';

const WriterTelemetry: React.FC = () => {
  const { enabled, loading: gateLoading } = useWriterEnabled();
  const [days, setDays] = useState(7);
  const { data, error, loading, reload, from, to } = useWriterTelemetry(days);

  const series = useMemo(
    () => (data ? buildDaySeries(data, from, to) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data],
  );
  const totals = useMemo(
    () => totalsFor(series, data?.users.length ?? 0),
    [series, data],
  );

  const dominantModel = useMemo(() => {
    const counts = new Map<string, number>();
    for (const r of data?.daily_model ?? []) counts.set(r.model, (counts.get(r.model) ?? 0) + r.turns);
    return [...counts.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? 'gemini-3.5-flash';
  }, [data]);

  if (gateLoading) return null;
  if (!enabled) return <Navigate to="/" replace />;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Navbar />
      <main className="flex-1 pt-24 pb-16 max-w-7xl mx-auto w-full px-4 space-y-10">
        <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-semibold">Writer Telemetry</h1>
            <p className="text-muted-foreground mt-1">
              Health, cost and agent behaviour for PaperVerse Writer. Costs are derived from token
              usage at Vertex list prices — the beta books no wallet debit.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {RANGES.map((r) => (
              <button
                key={r.days}
                onClick={() => setDays(r.days)}
                className={`px-3 py-1.5 text-sm rounded-md ${
                  days === r.days
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-secondary text-secondary-foreground'
                }`}
              >
                {r.label}
              </button>
            ))}
            <Button variant="outline" size="icon" onClick={() => void reload()} aria-label="Refresh">
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            <Button
              variant="outline"
              size="icon"
              aria-label="Export CSV"
              onClick={() =>
                exportCSV(
                  series.map((p) => ({
                    day: p.day,
                    turns: p.turns,
                    tool_calls: p.toolCalls,
                    iterations: p.iterations,
                    tokens_total: p.tokens.tokensTotal,
                    cost_eur: p.costEur.toFixed(4),
                    compiles_success: p.compilesSuccess,
                    compiles_failed: p.compilesFailed,
                  })),
                  'writer_telemetry',
                )
              }
            >
              <Download className="h-4 w-4" />
            </Button>
          </div>
        </header>

        {error && (
          <div className="text-destructive text-sm border border-destructive/40 rounded-md p-3">
            Could not load telemetry: {error}
          </div>
        )}

        {loading && !data ? (
          <p className="text-muted-foreground">Loading telemetry…</p>
        ) : (
          <>
            <HealthStrip series={series} totals={totals} previous={null} days={days} />
            <LatencyPanel from={from} to={to} />
            <CostPanel series={series} totals={totals} recentTurns={data?.recent_turns ?? []} />
            <ReliabilityPanel
              series={series}
              totals={totals}
              failureCodes={data?.failure_codes ?? []}
              queue={data?.queue ?? null}
              dailyStops={data?.daily_stops ?? []}
            />
            <AgentPanel
              series={series}
              totals={totals}
              tools={data?.tools ?? []}
              repairs={data?.repairs ?? null}
              repairUnresolved={data?.repair_unresolved ?? []}
            />
            <BreakdownTables
              users={data?.users ?? []}
              projects={data?.projects ?? []}
              recentTurns={data?.recent_turns ?? []}
              dominantModel={dominantModel}
            />

            <section className="space-y-4">
              <h2 className="text-lg font-semibold">Ops</h2>
              <WriterCompileQueuePanel />
              <WriterRepairKillSwitches />
              <details className="rounded-xl border border-border/60 bg-muted/10 p-4">
                <summary className="cursor-pointer text-xs uppercase tracking-widest text-muted-foreground">
                  Cost calibration
                </summary>
                <div className="mt-4">
                  <WriterPricingSuggester />
                </div>
              </details>
            </section>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default WriterTelemetry;
